import { merge } from 'rxjs';
import {
  delayWhen,
  map,
  scan,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  action$,
  collectRoutines,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import {
  APICurrentUser,
  EphemeralNotificationsPersonalSettings,
  PersonalSetting,
} from '@ardoq/api-types';
import { currentDate } from '@ardoq/date-time';
import {
  getEphemeralNotificationDismissedKeys,
  getEphemeralNotificationRecords,
  getRecordsToMigrate,
} from './utils';
import {
  EphemeralNotificationRecords,
  ephemeralNotificationsStateOperations,
} from './ephemeralNotificationsStateOperations';
import { setDevEphemeralNotificationOverride } from './devEphemeralNotificationOverrides';
import {
  addEphemeralNotificationResolution,
  EphemeralNotificationResolution,
  ephemeralNotificationInteracted,
  type EphemeralNotificationInteractedPayload,
  ephemeralNotificationViewed,
  type EphemeralNotificationViewedPayload,
} from './actions';
import {
  EphemeralNotificationKeys,
  type TrackableNotificationConfig,
} from './types';
import {
  type EphemeralNotificationTrackingEvent,
  trackEphemeralNotificationEvent,
} from './tracking';

/** Restricts `setPersonalSetting` to the two settings this module owns. */
type EphemeralNotificationsPersonalSettingsEntry =
  | [
      PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE,
      EphemeralNotificationsPersonalSettings,
    ]
  | [PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS, undefined];

const ALLOWED_PERSONAL_SETTINGS: ReadonlySet<PersonalSetting> = new Set([
  PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE,
  PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS,
]);

const persistEphemeralNotificationsPersonalSettings = (
  entries: EphemeralNotificationsPersonalSettingsEntry[]
): void => {
  const allowedEntries = entries.filter(([key]) =>
    ALLOWED_PERSONAL_SETTINGS.has(key)
  );
  allowedEntries.forEach(([key, value]) =>
    currentUserInterface.setPersonalSetting(key, value)
  );
};

type RecordsIntent =
  | {
      type: 'resolved';
      notificationKey: EphemeralNotificationKeys;
      resolution: EphemeralNotificationResolution;
    }
  | ({ type: 'viewed' } & EphemeralNotificationViewedPayload)
  | ({ type: 'interacted' } & EphemeralNotificationInteractedPayload)
  | { type: 'migrate' };

const resolveIntent$ = action$.pipe(
  ofType(addEphemeralNotificationResolution),
  extractPayload(),
  map(({ notificationKey, resolution }): RecordsIntent => ({
    type: 'resolved',
    notificationKey,
    resolution,
  }))
);

const viewIntent$ = action$.pipe(
  ofType(ephemeralNotificationViewed),
  extractPayload(),
  map((payload): RecordsIntent => ({ type: 'viewed', ...payload }))
);

const interactIntent$ = action$.pipe(
  ofType(ephemeralNotificationInteracted),
  extractPayload(),
  map((payload): RecordsIntent => ({ type: 'interacted', ...payload }))
);

/** Migrates any legacy-only dismissal into `EPHEMERAL_NOTIFICATIONS_STATE`, once per session. */
const migrateIntent$ = loadedCurrentUser$.pipe(
  take(1),
  map((): RecordsIntent => ({ type: 'migrate' }))
);

type TrackedEvent = {
  config: TrackableNotificationConfig;
  event: EphemeralNotificationTrackingEvent;
};

type RecordsStep = {
  records: EphemeralNotificationRecords | null;
  trackedEvent: TrackedEvent | null;
  write: EphemeralNotificationRecords | null;
  devOverrideKey: EphemeralNotificationKeys | null;
  clearLegacyDismissedKeys: boolean;
  currentUserState: APICurrentUser | null;
};

const initialRecordsStep: RecordsStep = {
  records: null,
  trackedEvent: null,
  write: null,
  devOverrideKey: null,
  clearLegacyDismissedKeys: false,
  currentUserState: null,
};

/**
 * Applies one intent on top of the previous intent's own result, rather
 * than `currentUserState` — which only reflects a write once its PATCH request
 * has round-tripped, so two intents processed close together would otherwise
 * compute from the same stale snapshot and the second write would silently
 * drop the first's change.
 */
const applyRecordsIntent = (
  acc: RecordsStep,
  [intent, currentUserState]: [RecordsIntent, APICurrentUser]
): RecordsStep => {
  const now = currentDate();
  const records =
    acc.records ?? getEphemeralNotificationRecords(currentUserState, now);

  switch (intent.type) {
    case 'migrate': {
      const recordsToMigrate = getRecordsToMigrate(currentUserState, now);
      // Every legacy dismissal is already folded into `records` above, so
      // once this migrate runs the ledger has nothing left to contribute.
      const legacyDismissedKeys =
        getEphemeralNotificationDismissedKeys(currentUserState);
      return {
        records,
        trackedEvent: null,
        write: recordsToMigrate,
        devOverrideKey: null,
        clearLegacyDismissedKeys: Boolean(legacyDismissedKeys?.length),
        currentUserState,
      };
    }
    case 'resolved': {
      const next = ephemeralNotificationsStateOperations.recordOutcome({
        records,
        notificationKey: intent.notificationKey,
        outcome: { status: intent.resolution },
        now,
      });
      return {
        records: next,
        trackedEvent: null,
        write: next,
        devOverrideKey: intent.notificationKey,
        clearLegacyDismissedKeys: false,
        currentUserState,
      };
    }
    case 'viewed': {
      const record = records[intent.config.id];
      if (!ephemeralNotificationsStateOperations.shouldCountView(record, now))
        return {
          records,
          trackedEvent: null,
          write: null,
          devOverrideKey: null,
          clearLegacyDismissedKeys: false,
          currentUserState,
        };
      const next = ephemeralNotificationsStateOperations.recordView({
        records,
        notificationKey: intent.config.id,
        now,
      });
      return {
        records: next,
        write: next,
        trackedEvent: ephemeralNotificationsStateOperations.hasNoCountedView(
          record
        )
          ? {
              config: intent.config,
              event: {
                type: 'seen',
                viewCount: next[intent.config.id].viewCount,
              },
            }
          : null,
        devOverrideKey: null,
        clearLegacyDismissedKeys: false,
        currentUserState,
      };
    }
    case 'interacted':
      return {
        records,
        trackedEvent: {
          config: intent.config,
          event: {
            ...intent.interaction,
            viewCount: records[intent.config.id]?.viewCount ?? 0,
          },
        },
        write: null,
        devOverrideKey: null,
        clearLegacyDismissedKeys: false,
        currentUserState,
      };
  }
};

const persistRecordsStep = ({
  write,
  trackedEvent,
  devOverrideKey,
  clearLegacyDismissedKeys,
  currentUserState,
}: RecordsStep): void => {
  const entries: EphemeralNotificationsPersonalSettingsEntry[] = [];
  if (write) {
    entries.push([
      PersonalSetting.EPHEMERAL_NOTIFICATIONS_STATE,
      { records: write },
    ]);
  }
  if (clearLegacyDismissedKeys) {
    // `undefined` drops the key from the PUT body entirely (see userApi.updateSettings)
    entries.push([
      PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS,
      undefined,
    ]);
  }
  persistEphemeralNotificationsPersonalSettings(entries);
  if (trackedEvent) {
    trackEphemeralNotificationEvent(trackedEvent.config, trackedEvent.event);
  }
  if (devOverrideKey && currentUserState) {
    setDevEphemeralNotificationOverride(currentUserState, devOverrideKey, true);
  }
};

/**
 * Queued until the user has loaded. Runs behind the migrate only because
 * `migrateIntent$` subscribes to `loadedCurrentUser$` before these do.
 */
const actionIntent$ = merge(resolveIntent$, viewIntent$, interactIntent$).pipe(
  delayWhen(() => loadedCurrentUser$)
);

const handleEphemeralNotificationIntents$ = merge(
  actionIntent$,
  migrateIntent$
).pipe(
  withLatestFrom(loadedCurrentUser$),
  // Builds on its own last result, not currentUserState, so back-to-back intents don't race.
  scan(applyRecordsIntent, initialRecordsStep),
  tap(persistRecordsStep)
);

export const ephemeralNotificationRoutines = collectRoutines(
  handleEphemeralNotificationIntents$
);
